import React from 'react';
import {
  Box,
  Grid,
  Typography,
  TextField,
  Stack,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Lock as LockIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Visibility,
  VisibilityOff,
} from '@mui/icons-material';
import { Card } from '../../UI/Card';
import { Button } from '../../UI/Button';
import { PasswordChangeFormProps } from './types';
import { validatePassword } from './utils';

export const PasswordChangeForm: React.FC<PasswordChangeFormProps> = ({
  passwordData,
  isChangingPassword,
  isUpdating,
  error,
  showPasswords,
  onPasswordChange,
  onPasswordSave,
  onPasswordCancel,
  onTogglePasswordVisibility,
  onStartChange,
  onClearError,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const passwordValidation = validatePassword(passwordData.newPassword);
  
  // 保存可能かどうかの判定
  const canSave = passwordData.currentPassword &&
                  passwordData.newPassword &&
                  passwordData.confirmPassword &&
                  passwordValidation.isValid &&
                  passwordData.newPassword === passwordData.confirmPassword;

  return (
    <Card
      title={
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <LockIcon color="primary" fontSize="large" />
          <Typography 
            variant="h5" 
            fontWeight="bold"
            sx={{ fontSize: { xs: '1.2rem', sm: '1.5rem' } }}
          >
            パスワード変更
          </Typography>
        </Box>
      }
      action={
        !isChangingPassword ? (
          <Button
            variant="outline"
            size={isMobile ? "small" : "medium"}
            startIcon={<LockIcon />}
            onClick={onStartChange}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            パスワード変更
          </Button>
        ) : (
          <Stack 
            direction={{ xs: 'column', sm: 'row' }} 
            spacing={1}
            sx={{ width: { xs: '100%', sm: 'auto' } }}
          >
            <Button
              variant="primary"
              size={isMobile ? "small" : "medium"}
              startIcon={<SaveIcon />}
              onClick={onPasswordSave}
              loading={isUpdating}
              disabled={isUpdating || !canSave}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              変更
            </Button>
            <Button
              variant="outline"
              size={isMobile ? "small" : "medium"}
              startIcon={<CancelIcon />}
              onClick={onPasswordCancel}
              disabled={isUpdating}
              sx={{ width: { xs: '100%', sm: 'auto' } }}
            >
              キャンセル
            </Button>
          </Stack>
        )
      }
      padding="large"
      sx={{ mt: 3 }}
    >
      {error && (
        <Alert severity="error" sx={{ mb: 3 }} onClose={onClearError}>
          {error}
        </Alert>
      )}

      {isChangingPassword ? (
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          <Grid item xs={12}>
            <TextField
              fullWidth
              label="現在のパスワード"
              type={showPasswords.current ? 'text' : 'password'}
              value={passwordData.currentPassword}
              onChange={onPasswordChange('currentPassword')}
              disabled={isUpdating}
              required
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onTogglePasswordVisibility('current')}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {showPasswords.current ? <VisibilityOff /> : <Visibility />}
                  </Button>
                ),
              }}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="新しいパスワード"
              type={showPasswords.new ? 'text' : 'password'}
              value={passwordData.newPassword}
              onChange={onPasswordChange('newPassword')}
              disabled={isUpdating}
              required
              error={passwordData.newPassword !== '' && !passwordValidation.isValid}
              helperText={
                passwordData.newPassword === '' 
                  ? "8文字以上、大文字・小文字・数字・記号(@$!%*?&)を含む"
                  : passwordData.newPassword && !passwordValidation.isValid
                    ? `要件不足: ${!passwordValidation.minLength ? '8文字以上, ' : ''}${!passwordValidation.hasLowerCase ? '小文字, ' : ''}${!passwordValidation.hasUpperCase ? '大文字, ' : ''}${!passwordValidation.hasNumber ? '数字, ' : ''}${!passwordValidation.hasSymbol ? '記号(@$!%*?&), ' : ''}`.replace(/, $/, '')
                    : "✓ パスワード要件を満たしています"
              }
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onTogglePasswordVisibility('new')}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {showPasswords.new ? <VisibilityOff /> : <Visibility />}
                  </Button>
                ),
              }}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
                '& .MuiFormHelperText-root': {
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              label="新しいパスワード（確認）"
              type={showPasswords.confirm ? 'text' : 'password'}
              value={passwordData.confirmPassword}
              onChange={onPasswordChange('confirmPassword')}
              disabled={isUpdating}
              required
              error={
                passwordData.confirmPassword !== '' &&
                passwordData.newPassword !== passwordData.confirmPassword
              }
              helperText={
                passwordData.confirmPassword !== '' &&
                passwordData.newPassword !== passwordData.confirmPassword
                  ? 'パスワードが一致しません'
                  : '確認のため再度入力してください'
              }
              InputProps={{
                startAdornment: <LockIcon color="action" sx={{ mr: 1 }} />,
                endAdornment: (
                  <Button
                    variant="text"
                    size="small"
                    onClick={() => onTogglePasswordVisibility('confirm')}
                    sx={{ minWidth: 'auto', p: 1 }}
                  >
                    {showPasswords.confirm ? <VisibilityOff /> : <Visibility />}
                  </Button>
                ),
              }}
              size={isMobile ? 'small' : 'medium'}
              sx={{
                '& .MuiInputBase-input': {
                  fontSize: { xs: '0.9rem', sm: '1rem' },
                },
                '& .MuiFormHelperText-root': {
                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                },
              }}
            />
          </Grid>

          <Grid item xs={12}>
            <Box sx={{ 
              p: { xs: 1.5, sm: 2 }, 
              bgcolor: 'info.light', 
              borderRadius: 1 
            }}>
              <Typography 
                variant="body2" 
                color="info.dark"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.875rem' } }}
              >
                🔒 <strong>パスワード要件：</strong><br />
                • 8文字以上<br />
                • 大文字・小文字・数字・記号(@$!%*?&)をそれぞれ1文字以上含む<br />
                • 他の人に推測されにくい、強力なパスワードを設定してください
              </Typography>
            </Box>
          </Grid>
        </Grid>
      ) : (
        <Box sx={{ textAlign: 'center', py: 3 }}>
          <LockIcon sx={{ fontSize: { xs: 36, sm: 48 }, color: 'text.secondary', mb: 2 }} />
          <Typography 
            variant="body1" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
          >
            セキュリティ向上のため、定期的なパスワード変更をお勧めします。
          </Typography>
        </Box>
      )}
    </Card>
  );
}; 